import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Edit } from '@mui/icons-material';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/components/icons';
import { SecureImage } from '@/components/secure-image';
import type { EntitySummary } from '@/lib/types';

interface CodexCardProps {
  entry: EntitySummary;
  categoryName: string;
}

export function CodexCard({ entry, categoryName }: CodexCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          borderRadius: 3,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px -10px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Link 
          to="/codex/view/$id" 
          params={{ id: entry.id }} 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
        >
        <CardMedia
          sx={{
            aspectRatio: '1 / 1',
            position: 'relative',
            bgcolor: 'action.hover',
            overflow: 'hidden',
          }}
        >
          {entry.mainImageUrl ? (
            <SecureImage
              src={entry.mainImageUrl}
              path={entry.mainImagePath}
              alt={entry.name}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s',
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.1 }}>
              <Icons.codex width={80} height={80} />
            </Box>
          )}
          {entry.type && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {categoryName}
            </Box>
          )}
        </CardMedia>
        <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: '1.1rem',
              mb: 1,
              color: 'text.primary',
              fontWeight: 600,
              pr: 5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {entry.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.5,
              fontSize: '0.85rem',
            }}
          >
            {entry.shortDescription}
          </Typography>
          {entry.updatedAt && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 'auto',
                pt: 2,
                color: 'text.disabled',
                fontSize: '0.75rem',
              }}
            >
              {t('entity_list.codex.updated_at')}: {new Date(entry.updatedAt).toLocaleString()}
            </Typography>
          )}
        </CardContent>
        </Link>
        <Tooltip title={t('entity_list.codex.edit')}>
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              navigate({ to: '/codex/edit/$id', params: { id: entry.id } });
            }}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'background.paper',
              boxShadow: 1,
              zIndex: 2,
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
            size="small"
          >
            <Edit sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Card>
  );
}
